import React, { useState, useEffect } from 'react';
import { Button, Input, PopUpBox, ThresholdWarningPopup } from '../../../shared';
import { vendorModalDispatch, saveAsDraftManufacturing } from '../../../../service';
import { useDebounce } from '../../../../hooks';
import { getLocalStorageItem, popup } from '../../../../utils';
import { BUTTON, DISPLAY_TEXTS, LOCAL_STORAGE } from '../../../../constants';

export const FabricProductionModal = ({ closePopUp, modalData, setReload }) => {
  const [products, setProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [rows, setRows] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [producingQuantity, setProducingQuantity] = useState(
    modalData.demand_quantity !== modalData.producing_quantity
      ? (modalData.demand_quantity - modalData.producing_quantity).toFixed(2).toString()
      : modalData.demand_quantity.toFixed(2).toString(),
  );
  const [damagedQuantity, setDamagedQuantity] = useState(0);
  const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
  const isFinishedGoods = vendorType === 'finished_goods';
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null);

  const getProductsList = useDebounce(() => {
    vendorModalDispatch({
      data: { move_id: modalData.move_id },
    })
      .then((response) => {
        if (response.message?.toString().toLowerCase().includes('not')) popup('error', response.message);
        else setProductsList(response.data);
      })
      .catch((err) => {
        console.error(err)
      });
  }, 400);

  useEffect(() => {
    getProductsList();
  }, []);

  useEffect(() => {
    if (productsList.length > 0) {
      const data = productsList.map((product) => ({
        move_id: product.id,
        name: product?.display_name?.toString() || '',
        defaultLotNo: product?.selected_lot?.[0]?.name || '',
        defaultLotId: product?.selected_lot?.[0]?.id || '',
        defaultLotAvailableQty: product?.selected_lot?.[0]?.available_qty || '0',
        tracking: product?.tracking || '',
        lotNumbers: product?.lots?.map((lot) => lot.name) || [],
        availableQtyList: product?.lots || [],
        unitMultiplier: product?.quantity || 0,
      }));
      const initialRows = data.map((product) => ({
        move_id: product.move_id,
        product: product.name,
        dropdown1: product.defaultLotNo,
        defaultLotId: product.defaultLotId,
        available_qty: product.defaultLotAvailableQty,
        producingQuantity: '',
        calculatedQuantity: '',
        unitMultiplier: product.unitMultiplier,
        availableQtyList: product.availableQtyList,
      }));

      setRows(initialRows);
      setProducts(data);
    }
  }, [productsList]);

  useEffect(() => {
    const allFilled = rows.every((row) => row.calculatedQuantity !== '');
    if (Array.isArray(rows) && rows.length > 0) setIsSubmitDisabled(!allFilled);
  }, [rows]);

  const handleChange = (field, value, rowIndex) => {
    const newRows = [...rows];
    if (field === 'producingQuantity') {
      setProducingQuantity(value);
      setIsSaveDisabled(value <= 0);
    } else if (field === 'dropdown1') {
      newRows[rowIndex][field] = value;
      const selectedLot = newRows[rowIndex].availableQtyList.find((lot) => lot.name === value);
      newRows[rowIndex].available_qty = selectedLot ? selectedLot.available_qty : '0';
      newRows[rowIndex].lot_id = selectedLot ? selectedLot.id : rows[0].defaultLotId;
    }
    setRows(newRows);
  };

  const handleSave = () => {
    const vendor_type = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

    if (vendor_type === 'fabric') {
      const newRows = rows.map((row) => {
        const usedQty = Math.min(row.available_qty, parseFloat(producingQuantity));
        const calculatedQuantity = (usedQty * row.unitMultiplier).toFixed(4);

        return {
          ...row,
          producingQuantity,
          calculatedQuantity,
          lot_id: row.lot_id || row.defaultLotId,
        };
      });

      setRows(newRows);
    }
    else {
      let remainingQty = parseFloat(producingQuantity);

      const newRows = rows.map((row) => {
        const usedQty = Math.min(row.available_qty, remainingQty);
        const calculatedQuantity = (usedQty * row.unitMultiplier).toFixed(4);
        remainingQty -= usedQty;

        return {
          ...row,
          producingQuantity,
          calculatedQuantity,
          lot_id: row.lot_id || row.defaultLotId,
        };
      });

      setRows(newRows);
    }
  };

  const handleSubmit = useDebounce(async () => {
    // Clear any previous pending callbacks and popup state
    if (pendingSaveCallback) {
      setPendingSaveCallback(null);
    }
    if (showThresholdWarning) {
      setShowThresholdWarning(false);
      setThresholdWarningData(null);
    }

    const move_ids = [
      {
        move_id: modalData.move_id,
        producing_quantity: parseFloat(producingQuantity),
        damaged_quantity: parseFloat(damagedQuantity),
      },
    ];
    const picking_ids = [];
    const container_number = '';

    try {
      // ALWAYS call with force_submit=false first - popup will show if threshold exceeded
      const response = await saveAsDraftManufacturing({ 
        data: { 
          move_ids, 
          picking_ids, 
          container_number,
          force_submit: false 
        } 
      });

      // Check if response is a warning
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        // Show warning popup and wait for user confirmation
        setThresholdWarningData(response);
        setShowThresholdWarning(true);
        setPendingSaveCallback({
          move_ids: move_ids,
          picking_ids: picking_ids,
          container_number: container_number,
          onComplete: async (saveSuccess) => {
            if (saveSuccess) {
              setReload(true);
              closePopUp(false);
            }
          },
          onCancel: () => {
            // User cancelled - keep modal open with current values
            setPendingSaveCallback(null);
          }
        });
        return; // Wait for user confirmation
      }

      // No warning - proceed normally
      if (response && response.message) {
        popup('success', response.message);
        setReload(true);
        closePopUp(false);
      } else {
        popup('error', 'Save as draft failed');
      }
    } catch (error) {
      popup('error', error.message);
    }
  }, 400);


  const handleChangeDamagedQuantity = (value) => {
    const numericValue = value === '' ? '' : Math.max(0, Number(value)); // Ensure non-negative value or allow clearing
    setDamagedQuantity(numericValue);
  };

  const handleThresholdWarningConfirm = async (editedValues = []) => {
    setShowThresholdWarning(false);
    
    // Handle save_as_draft callback - only proceed if callback exists
    if (!pendingSaveCallback) {
      console.warn("handleThresholdWarningConfirm called but no pendingSaveCallback exists");
      return;
    }
    
    // Update move_ids with edited quantities if provided (handle both produce and damage)
    let move_ids = [];
    if (editedValues && editedValues.length > 0 && pendingSaveCallback.move_ids) {
      // Update existing move_ids with edited quantities
      move_ids = pendingSaveCallback.move_ids.map(move => {
        // Convert both to numbers for comparison
        const moveIdNum = parseInt(move.move_id);
        const editedProduce = editedValues.find(ev => {
          const evMoveIdNum = parseInt(ev.moveId);
          return evMoveIdNum === moveIdNum && ev.type === 'produce';
        });
        const editedDamage = editedValues.find(ev => {
          const evMoveIdNum = parseInt(ev.moveId);
          return evMoveIdNum === moveIdNum && ev.type === 'damage';
        });
        
        const updatedMove = { ...move };
        if (editedProduce) {
          updatedMove.producing_quantity = parseFloat(editedProduce.quantity) || 0;
        }
        if (editedDamage) {
          updatedMove.damaged_quantity = parseFloat(editedDamage.quantity) || 0;
        }
        return updatedMove;
      });
    } else {
      move_ids = pendingSaveCallback.move_ids || [];
    }
    
    // Re-call save_as_draft with force_submit=true ONLY when user confirms on popup
    const { picking_ids, container_number } = pendingSaveCallback;
    
    try {
      const response = await saveAsDraftManufacturing({ 
        data: { 
          move_ids, 
          picking_ids, 
          container_number,
          force_submit: true 
        } 
      });
      
      // Check if response is successful (has message and no warning status)
      const isSuccess = response && response.message && response.status !== 'warning';
      
      // Execute the callback with the result
      if (pendingSaveCallback.onComplete) {
        pendingSaveCallback.onComplete(isSuccess);
      }
      
      if (isSuccess) {
        popup('success', response?.message);
      } else {
        popup('error', 'Save as draft failed');
      }
    } catch (error) {
      console.error("Failed to save draft after confirmation:", error);
      popup('error', error.message || 'Failed to save draft');
      if (pendingSaveCallback.onComplete) {
        pendingSaveCallback.onComplete(false);
      }
    }
    
    // Clear callback after use
    setPendingSaveCallback(null);
  };

  const handleThresholdWarningCancel = () => {
    setShowThresholdWarning(false);
    setThresholdWarningData(null);
    
    // Handle save_as_draft cancellation
    if (pendingSaveCallback && pendingSaveCallback.onCancel) {
      pendingSaveCallback.onCancel();
    }
    setPendingSaveCallback(null);
  };

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title w-100" id="fabricDispatchModalLabel">
          {DISPLAY_TEXTS.FABRIC_DISPATCH[0]}
        </h5>
      </div>
      <div className="modal-body">
        <div className="w-100 editing-container">
          <div className="row d-flex align-items-center justify-content-center">
            <div className="edit-container py-3 px-lg-4 px-4 table-new-div bg-white data-box w-100 h-100">
              {/* Detail Box */}
              <div className="detail-box-popup mb-3">
                {!isFinishedGoods && (
                  <div className="row mb-1">
                    <div className="col-12">
                      <strong>{DISPLAY_TEXTS.FABRIC_DISPATCH[1]}</strong> {modalData.lotno}
                    </div>
                  </div>
                )}
                <div className="row mb-1">
                  <div className="col-12">
                    <strong>{DISPLAY_TEXTS.FABRIC_DISPATCH[2]}</strong>{' '}
                    {modalData.demand_quantity.toFixed(2)}
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-12">
                    <strong>{DISPLAY_TEXTS.FABRIC_DISPATCH[4]}</strong>{' '}
                    {modalData.producing_quantity.toFixed(2)}
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <strong>{DISPLAY_TEXTS.FABRIC_DISPATCH[3]}</strong>
                      <Input
                        className="prd-qty-input"
                        type="number"
                        value={producingQuantity}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => handleChange('producingQuantity', e.target.value)}
                      />
                      <span className="m-1">
                        {' '}
                        /{' '}
                        {modalData.demand_quantity !== modalData.producing_quantity
                          ? (modalData.demand_quantity - modalData.producing_quantity).toFixed(2)
                          : modalData.demand_quantity.toFixed(2)}
                      </span>
                      <Button
                        className={`m-1 border-0 blue-btn align-items-center justify-content-center py-2 px-3 ${isSaveDisabled ? 'disabled' : ''}`}
                        clickHandler={handleSave}
                        disabled={isSaveDisabled}>
                        {BUTTON.SAVE}
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Removed Save Button for Damaged Quantity */}
                <div className="row mb-1">
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <strong>Damaged Quantity: </strong>
                      <Input
                        className="prd-qty-input"
                        type="number"
                        value={damagedQuantity}
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => handleChangeDamagedQuantity(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="w-100 table">
                  <table className="w-100">
                    <thead>
                      <tr>
                        {DISPLAY_TEXTS.FABRIC_DISPATCH_MODAL.map((column, index) =>
                          (products?.[0]?.tracking !== 'lot' &&
                            column === 'Lot No.' &&
                            getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'dyeing') ||
                            (isFinishedGoods && column === 'Lot No') ? null : (
                            <th className={`align-items-center custom-padding`} key={index}>
                              {column}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="inner-table-tbody-input">
                      {rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>
                            <span className="display-row-text">{row.product}</span>
                          </td>
                          {products?.[0]?.tracking !== 'lot' &&
                            getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'dyeing' || isFinishedGoods ? null : (
                            <td>
                              <span className="display-row-text">{row.dropdown1}</span>
                            </td>
                          )}
                          <td className="custom-padding">
                            <span className="display-row-text px-2">{row.available_qty}</span>
                          </td>
                          <td className="custom-padding">
                            <span className="display-row-text px-2">{row.calculatedQuantity}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <Button
          className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${damagedQuantity === '' || damagedQuantity < 0 || isSubmitDisabled ? 'disabled' : ''}`}
          clickHandler={handleSubmit}
          disabled={damagedQuantity === '' || damagedQuantity < 0 || isSubmitDisabled}>
          {BUTTON.TRUE_SUBMIT}
        </Button>
        <Button
          className="border-0 grey-btn d-flex align-items-center justify-content-center py-2 px-3"
          clickHandler={() => closePopUp(false)}>
          {BUTTON.CLOSE}
        </Button>
      </div>
      {showThresholdWarning && thresholdWarningData && (
        <PopUpBox openPopUpBox={showThresholdWarning}>
          <ThresholdWarningPopup
            warningData={thresholdWarningData}
            onConfirm={handleThresholdWarningConfirm}
            onCancel={handleThresholdWarningCancel}
            allowEdit={true}
          />
        </PopUpBox>
      )}
    </>
  );
};
